import type { Column, IHeaderGroupParams } from 'ag-grid-community';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon, WarningAmber as WarningIcon } from '@mui/icons-material';

import React, { FC } from 'react';
// TODO: remove importing getCurrentAgGridContext until state management resolve in REP-8620
import { AG_GRID_NEW_COLUMN_PLACEHOLDER_ID } from '../../agGridConsts';
import { useIsEditing } from '../../useIsEditing';
import { ColumnFiltersTooltip } from './ColumnFiltersTooltip';
import {
  IColumnFilterMDS,
  IProjection,
  IColumnGroupColumn,
  RelativeFilter,
  ISingleValueColumnFilter,
  IReportDatasetLens
} from '@opengov/reporting-types';
import { FxBadge } from '../../../editor/sidepanel/Calculations/FxBadge';
import type { BrokenCalcColumn } from '../../../editor/sidepanel/Calculations/findBrokenCalcColumns';
import { brokenCalcColumnTooltip } from '../../../editor/sidepanel/Calculations/brokenCalcColumnText';
import styles from './MdsColumnHeader.module.scss';
import { TooltipLocaleText } from '../types';

interface IHeaderProps extends IHeaderGroupParams {
  asOfDate?: string;
  dataTest?: string;
  dateFilter: ISingleValueColumnFilter<IColumnGroupColumn>[];
  relativeFilter: RelativeFilter;
  filters: IColumnFilterMDS<IProjection>[];
  entityFiscalYearStartMonth: number;
  rolling?: number;
  selected?: boolean;
  column?: Column;
  showHiddenColumnGroupsIndicator?: boolean;
  onClick(): void;
  lens: IReportDatasetLens;
  text: TooltipLocaleText;
  isCalculated?: boolean;
  /** Set when this calc column renders blank because its formula references absent columns. */
  brokenCalcColumn?: BrokenCalcColumn;
}

export const MdsColumnHeader: FC<IHeaderProps> = (props) => {
  const {
    columnGroup,
    displayName,
    dataTest,
    selected,
    showHiddenColumnGroupsIndicator,
    dateFilter,
    relativeFilter,
    filters,
    lens,
    text,
    entityFiscalYearStartMonth,
    rolling,
    asOfDate,
    isCalculated,
    brokenCalcColumn
  } = props;

  const handleClick = () => {
    const { api, columnGroup, column, onClick } = props;

    // clear selections
    api.clearRangeSelection();

    // Check current selected rows, if there is rows selected remove them
    const previousSelectedRows = api.getSelectedRows();
    if (previousSelectedRows?.length > 0) {
      api.forEachNode((row) => {
        row.setSelected(false);
      });
    }

    // Get columns to include in the range selection
    let columnsToSelect: Column[] = [];

    if (column) {
      // If it's an individual column, select just that column
      columnsToSelect = [column];
    } else {
      // If it's a column group, select all columns in the group
      const groupId = columnGroup.getGroupId();
      columnsToSelect = api.getAllDisplayedColumns().filter((col) => col.getParent()?.getGroupId() === groupId);
    }

    // highlight columns
    api.addCellRange({
      columns: columnsToSelect,
      rowStartIndex: 0,
      rowEndIndex: Infinity
    });

    onClick();
  };

  const isPlaceholder = columnGroup?.getLeafColumns()[0]?.getColId() === AG_GRID_NEW_COLUMN_PLACEHOLDER_ID;
  const isEditing = useIsEditing();
  // const showFiltersTooltip = !isEditing && !!filtersTooltipContent?.totalAppliedFilters;
  const showEditModeButton = isEditing && !isPlaceholder;

  return (
    <Box role="presentation" className={styles.groupHeader}>
      <Box className={showHiddenColumnGroupsIndicator ? styles.hiddenColumnGroupIndicator : styles.groupName}>
        {displayName}
      </Box>
      {brokenCalcColumn ? (
        <Tooltip placement="top" title={brokenCalcColumnTooltip(brokenCalcColumn)}>
          {/* A button so a keyboard user can focus it and get the explanation, which is also its
              name. It only explains, so a click must not reach the header's own handlers. */}
          <IconButton
            size="small"
            aria-label={brokenCalcColumnTooltip(brokenCalcColumn)}
            data-test="broken-calc-column-warning"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <WarningIcon fontSize="small" color="warning" />
          </IconButton>
        </Tooltip>
      ) : null}
      {!isEditing && (
        <ColumnFiltersTooltip
          entityFiscalYearStartMonth={entityFiscalYearStartMonth}
          rolling={rolling}
          dateFilter={dateFilter}
          relativeFilter={relativeFilter}
          filters={filters}
          lens={lens}
          text={text}
          dataTest={dataTest}
          asOfDate={asOfDate}
        />
      )}
      {showEditModeButton && (
        <>
          <Tooltip placement="top" title="Show Column Editor">
            <IconButton
              aria-pressed={selected}
              data-test={dataTest}
              onClick={handleClick}
              aria-label="show column editor"
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isCalculated ? (
            <Box className={styles.fxBadge}>
              <FxBadge />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};
